/**
 * What an error screen does next — retry, reload or report — shared by the
 * root, global and segment boundaries.
 *
 * 🚨 IMPORT-FREE, like `transient-network.ts` and for the same reason: the root
 * and global boundaries must render when a chunk has failed to load, so nothing
 * they depend on may drag a graph behind it. The Sentry call is passed IN by
 * the boundary rather than imported here (as `Sentry` itself, for its
 * `captureMessage` and `logger`), and so are `reloadsHeld` and the
 * page-leaving signal.
 *
 * ── THE RETRY LATCH LIVES HERE, NOT IN THE COMPONENT ───────────────────────
 *
 * Every boundary allows one silent retry for a dropped connection, and the
 * "one" used to be a `useState` inside the error screen. But nothing renders
 * an error screen directly: Next's `ErrorBoundaryHandler` does, and its
 * `reset()` renders the CHILDREN in the screen's place, which unmounts it. If
 * the children throw again a brand-new screen mounts with brand-new state, so
 * the latch was always empty when it mattered: a connection that stayed down
 * retried for ever behind a blank screen and reported nothing (MUCKA-WEB-A2,
 * 8 Oct 2026; 200+ renders measured through the real handler before this).
 *
 * Module state survives the remount. It is keyed per boundary, so the root
 * retrying does not spend the authed segment's attempt, and it expires, so a
 * NEW dropped connection a minute later still gets its quiet second chance.
 */

/**
 * How long a spent retry stays spent AFTER the screen that spent it has gone.
 * Short enough that a later, unrelated drop still retries.
 *
 * 🚨 Counted from the screen leaving, never from the retry starting. Once the
 * retry fetched the page it could take far longer than this on a bad signal,
 * and a clock started at the retry would expire mid-flight: a refresh that
 * streams for 35s and then dies would mount a screen that found the latch
 * free and retried again, and again, for as long as each attempt outlasted
 * the window — MUCKA-WEB-A2's endless retry, slowed down. The screen stays
 * mounted for the whole of its retry (it is a transition), so while it is up
 * the latch is held, and a failed retry's next screen mounts in the same
 * commit the old one leaves.
 */
const RETRY_WINDOW_MS = 30_000;

/**
 * Boundaries whose retry is spent. A set, not a timestamp, on purpose: the
 * screens read this DURING RENDER (in a `useState` initialiser), and a clock
 * read there is the hydration-mismatch shape `nothing-reads-a-fresh-clock-
 * while-rendering` exists to stop. The expiry runs on a timer instead, started
 * when the screen that spent the retry goes (see RETRY_WINDOW_MS).
 */
const spent = new Set<string>();
const expiries = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Spent retries the app has been put away since. Each one is free again.
 *
 * 🚨 Both halves of the latch assume the app keeps running, and on a phone it
 * does not. iOS suspends the web view when the app is put away: no timer runs,
 * so RETRY_WINDOW_MS never passes while it is away, and a page fetch still
 * streaming through the service worker dies when it comes back. An engineer
 * handed a job's address to the maps app, came back, and the root boundary
 * spent its retry; the app went away again with that retry still loading, and
 * two and a half hours later its death on return found the latch spent and
 * showed "That didn't load" with the signal up (MUCKA-WEB-CH, 19 Oct 2026).
 *
 * A failure after the app was away is a new one, not the retry failing. It
 * cannot loop: every extra retry needs the app put away and brought back
 * first, which a dead connection never does by itself, and a retry that dies
 * with the app in front is shown as before.
 */
const awaySinceSpent = new Set<string>();
let watchingForAway = false;

function watchForAway(): void {
  if (watchingForAway || typeof document === 'undefined') return;
  watchingForAway = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') return;
    for (const boundary of spent) awaySinceSpent.add(boundary);
  });
}

/** May this boundary spend a silent retry now? Read once, when the screen mounts. */
export function boundaryRetryAvailable(boundary: string): boolean {
  return !spent.has(boundary) || awaySinceSpent.has(boundary);
}

/** Spend it. Called from the effect that runs the retry, never from render. */
export function noteBoundaryRetry(boundary: string): void {
  spent.add(boundary);
  awaySinceSpent.delete(boundary);
  watchForAway();
  // A pending expiry belongs to an earlier screen (or StrictMode's rehearsal
  // of this one); the attempt now in flight holds the latch until it ends.
  const pending = expiries.get(boundary);
  if (pending !== undefined) clearTimeout(pending);
  expiries.delete(boundary);
}

/** The screen that spent it has gone: start the clock. From that effect's cleanup. */
export function releaseBoundaryRetry(boundary: string): void {
  if (typeof setTimeout !== 'function') return;
  const pending = expiries.get(boundary);
  if (pending !== undefined) clearTimeout(pending);
  expiries.set(boundary, setTimeout(() => {
    spent.delete(boundary);
    awaySinceSpent.delete(boundary);
    expiries.delete(boundary);
  }, RETRY_WINDOW_MS));
}

/** Test seam. */
export function resetBoundaryRetriesForTests(): void {
  for (const pending of expiries.values()) clearTimeout(pending);
  expiries.clear();
  spent.clear();
  awaySinceSpent.clear();
}

/**
 * How long an error screen stays blank while its silent retry is in flight.
 *
 * 🚨 `retry()` runs in a transition, so nothing renders until the fresh page
 * arrives, and the error screen stays mounted the whole time. Flipping it to
 * "shown" straight away — as the reset-only retry could, because `reset()`
 * unmounted it in the same commit — painted the error over a retry that was
 * about to work, and reported it as `shown_after_retry`. A retry that FAILS
 * needs no timer: the page throws again and Next mounts a new screen, which
 * finds the latch spent and shows itself. This only covers a refresh that
 * neither lands nor fails, so the owner is not left looking at nothing.
 */
export const RETRY_GRACE_MS = 8_000;

/**
 * "Try again" for an error that came off the network: fetch the page again,
 * then render it.
 *
 * 🚨 Next hands every error screen two functions and only one of them
 * fetches. `reset()` is `setState({ error: null })`: it renders the children
 * again from the router state already held, so a page whose server data died
 * mid-stream reads the same dead stream and throws the same error, every time.
 * `retry()` calls `router.refresh()` and THEN resets, which is the only way
 * the page's data is asked for again. Every boundary spent its one silent
 * retry on `reset()`, so a dropped RSC stream could never be recovered by it:
 * MUCKA-WEB-CH was "Schedule Failed to Load", `shown_after_retry`, on a phone
 * with signal (8 Oct 2026).
 *
 * The one time to keep to `reset()` is while reloads are held. A refresh that
 * cannot reach the server at all makes Next give up on the in-place update
 * and load the URL as a new document — during a live call that is the call
 * ending, which is what `reload-guard` exists to stop. Read at the moment of
 * the retry, not at render, because a call can start while the screen is up.
 *
 * So the SILENT retry does not come here while reloads are held: it waits for
 * the hold to lift and fetches then (`silentRetry`). "Try again" still does.
 *
 * ⚠️ Known limit, accepted 8 Oct 2026: the hold is read when the retry
 * STARTS, and the new-document load happens when its fetch FAILS. A call
 * started on a segment's "Reconnecting…" screen (the shell, and the call
 * button, stay up) in that gap still ends if the fetch then fails outright.
 * Next performs that load itself, so nothing here can refuse it.
 *
 * `reloadsHeld` is passed in rather than imported, so this file stays
 * import-free (see the root and global boundaries).
 */
export function tryAgain(
  retry: (() => void) | undefined,
  reset: () => void,
  reloadsHeld: () => boolean,
): void {
  if (retry && !reloadsHeld()) retry();
  else reset();
}

/** `@/lib/reload-guard`, passed in by the boundary so this file stays import-free. */
export type ReloadGuard = {
  reloadsHeld: () => boolean;
  onReloadsReleased: (fn: () => void) => () => void;
};

/**
 * A boundary's ONE silent retry: fetch the page again now, or — while a call
 * holds reloads off — the moment the call ends. Returns what to call if the
 * screen goes before then.
 *
 * 🚨 While reloads are held this used to be `reset()`, through `tryAgain`. A
 * page whose server data died mid-stream throws that straight back, so the
 * retry was spent on nothing and the owner got the error screen for the rest
 * of the call, with a "Try again" bound by the same rule (MUCKA-WEB-CH again,
 * 15 Oct 2026: a native call on /overview, the network fine throughout, and
 * not one fresh /overview request reaching the server). Waiting costs the call
 * nothing; refreshing during it could end it (see `tryAgain`).
 *
 * The hold can be anybody's, a form with edits in a sheet included; the wait
 * is the same either way.
 */
export function silentRetry(
  retry: (() => void) | undefined,
  reset: () => void,
  guard: ReloadGuard,
): () => void {
  if (!retry || !guard.reloadsHeld()) {
    tryAgain(retry, reset, guard.reloadsHeld);
    return () => {};
  }
  const off = guard.onReloadsReleased(() => {
    off();
    retry();
  });
  return off;
}

/**
 * A piece of the app's JavaScript that could not be fetched.
 *
 * Turbopack throws `Error('Failed to load chunk <url> from module <id>')` with
 * `name = 'ChunkLoadError'`; the other wordings are webpack's and the browsers'
 * own for a dynamic `import()`, kept because the boundary must not care which
 * bundler built the page it is rescuing.
 *
 * Checked BEFORE `isTransientNetworkError`: Turbopack appends the cause, which
 * can read "Failed to fetch", and a chunk error is never worth a re-render —
 * `React.lazy` keeps its rejected import and throws it again without fetching.
 */
const CHUNK_LOAD = /failed to load chunk|loading (css )?chunk [\w-]+ failed|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

export function isChunkLoadError(error: { name?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  return CHUNK_LOAD.test(error.message || '');
}

/** The chunk's path, which names the build it belongs to. No query string, no origin. */
export function chunkUrlOf(error: { message?: string } | null | undefined): string | null {
  const match = (error?.message || '').match(/\/_next\/static\/[^\s?#'"]+/);
  return match ? match[0] : null;
}

/**
 * What a boundary did about a chunk that would not load. `reloaded` means the
 * page is being loaded again and nobody saw the screen; `left` means a
 * full-page load somebody else started was already replacing the page (which
 * is very often WHY the chunk failed — see `page-leaving.ts`), so nobody saw
 * it either. `offline` is a screen somebody read, shown instead of a reload
 * because the device had no network (MUCKA-WEB-D3). The other two are screens
 * somebody read.
 */
export type ChunkOutcome = 'reloaded' | 'left' | 'offline' | 'shown' | 'shown_after_reload';

/** Outcomes nobody saw: logged, never an issue. See `reportChunkLoadFailure`. */
function unseen(outcome: ChunkOutcome): boolean {
  return outcome === 'reloaded' || outcome === 'left';
}

/**
 * Did the diagnosis prove there is no network to reload into? Both halves,
 * because each alone has a reading that a reload would fix: `onLine === false`
 * is conclusive about the interface but a refetch that came back means it has
 * since returned, and a refetch that failed with the interface up is the blip
 * the reload exists for (MUCKA-WEB-CV).
 */
function offlineNow(diagnosis: ChunkDiagnosis): boolean {
  let online: boolean | undefined;
  try { online = navigator.onLine; } catch { /* unknown is not offline */ }
  return online === false && diagnosis.network === 'unreachable';
}

/** Which of our own full-page loads is replacing the page, if any (`@/lib/page-leaving`). */
export type PageLeavingNow = { reason: string; msAgo: number } | null;

/**
 * `@/lib/page-leaving`, passed in by the boundary for the same reason as
 * `reloadsHeld`: this file stays import-free.
 */
export type PageLeavingSignal = {
  pageLeaving: () => PageLeavingNow;
  notePageLeaving: (reason: 'chunk-reload') => void;
};

/**
 * The mark that bounds the automatic reload. In sessionStorage rather than
 * module state, because the reload is the whole point and module state does
 * not survive it.
 */
const CHUNK_RELOAD_KEY = 'mucka_chunk_reload_at';

/**
 * How long one automatic reload stays spent. A chunk that fails again on the
 * page that reload produced is not a blip, and reloading again would be a loop;
 * a chunk failing an hour later is a new blip and gets its own reload.
 */
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

/**
 * Load the page again, once, for a chunk that would not download.
 *
 * 🚨 The silent retry every boundary spends on a dropped DATA fetch never
 * reached a dropped CHUNK fetch, because re-rendering cannot fetch one —
 * `React.lazy` throws its cached failure straight back. A fresh document can,
 * and that is all the screen's "Try again" ever did. So the screen was shown,
 * and the one button on it pressed, for what was usually a blip: MUCKA-WEB-CV
 * was a chunk that is on production with a 200, failing twice through
 * Turbopack's own retry on an iPhone 1.8 seconds after a cold start, while
 * every request around it succeeded (11 Oct 2026).
 *
 * Bounded four ways, each of which shows the screen instead:
 *   · a reload already spent inside CHUNK_RELOAD_WINDOW_MS — the chunk failed
 *     on the fresh document too, which is worth showing;
 *   · a live call holding reloads off — reloading it is hanging up on the
 *     customer (`reload-guard`), and `reloadsHeld` is passed in so this file
 *     stays import-free. Asked again just before the reload, because a call
 *     can start while the diagnosis runs;
 *   · no way to write the mark — with nothing to bound it, one bad chunk would
 *     reload for ever, which is worse than any screen;
 *   · no network at all — the interface is down and the refetch never reached
 *     the server (MUCKA-WEB-D3, 13 Oct 2026). The fresh document comes out of
 *     the same cache and asks for the same file through the same dead network,
 *     so the reload only failed again a second later and filed the result as
 *     `shown_after_reload`, the error kept for a chunk missing from the build.
 *     The mark is left unspent, so a failure once the signal is back still
 *     gets its reload.
 *
 * The screen shows straight away when no reload is coming. When one is, the
 * report goes first (with its diagnosis), because nothing survives the reload.
 *
 * 🚨 And never a second full-page load on top of one already under way
 * (`pageLeaving`). A chunk failing at that moment was usually cancelled BY that
 * load, the page is going anyway, and a reload started over it cancels it in
 * turn — which Capacitor answers with the bundled offline page. Asked before
 * the diagnosis and again after it, because an update can start a reload
 * while the diagnosis runs. The screen stays blank; the boundary's own
 * RETRY_GRACE_MS timer shows it if the page somehow stays.
 *
 * Call it from an effect, never during render: it reads the clock and it
 * navigates.
 */
export function recoverFromChunkFailure(
  sentry: ChunkReporter,
  boundary: string,
  error: { name?: string; message?: string },
  reloadsHeld: () => boolean,
  showScreen: () => void,
  leaving: PageLeavingSignal,
): void {
  // Nothing to diagnose: the refetch would only be cancelled by the same load.
  const already = leaving.pageLeaving();
  if (already) {
    reportChunkLoadFailure(sentry, boundary, error, 'left', undefined, already);
    return;
  }
  const planned = chunkReloadPlanned(reloadsHeld);
  if (planned !== 'reloaded') showScreen();
  void diagnoseChunkFailure(chunkUrlOf(error)).then((diagnosis) => {
    const now = leaving.pageLeaving();
    const outcome: ChunkOutcome = planned !== 'reloaded'
      ? planned
      : now
        ? 'left'
        : offlineNow(diagnosis)
          ? 'offline'
          : markChunkReload(reloadsHeld) ? 'reloaded' : 'shown';
    reportChunkLoadFailure(sentry, boundary, error, outcome, diagnosis, now);
    if (outcome === 'reloaded') {
      leaving.notePageLeaving('chunk-reload');
      window.location.reload();
    } else if ((outcome === 'shown' || outcome === 'offline') && planned === 'reloaded') {
      showScreen();
    }
  });
}

/** Would a reload be allowed now? Reads the mark; writes nothing. */
function chunkReloadPlanned(reloadsHeld: () => boolean): ChunkOutcome {
  let last: number;
  try {
    last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY));
  } catch {
    return 'shown';
  }
  if (last > 0 && Date.now() - last < CHUNK_RELOAD_WINDOW_MS) return 'shown_after_reload';
  return reloadsHeld() ? 'shown' : 'reloaded';
}

/** Spend the reload: false if a call has started or the mark cannot be written. */
function markChunkReload(reloadsHeld: () => boolean): boolean {
  if (reloadsHeld()) return false;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/**
 * What the phone can still tell us about WHY the chunk failed, asked at the
 * moment of failure. One event of MUCKA-WEB-CV could not distinguish the
 * suspects, because a script load leaves no breadcrumb and the service worker
 * reports nothing:
 *
 *   · `swUpdate` — was a new service worker `installing` or `waiting`? A deploy
 *     makes the next cold start install one, and install pre-downloads the
 *     offline pages and every chunk they name, in the same seconds somebody is
 *     tapping their first page. That event was a cold start 31 minutes after a
 *     deploy.
 *   · `chunkCached` — was the file already in the worker's cache? The worker
 *     answers a cached chunk without the network, so `true` beside a failed
 *     load points at a cache entry that could not be read, not the signal.
 *   · `refetch` — what the same URL gets now: a status, the worker's own
 *     "no network" 504, or the rejection's words. A 200 means it was a blip;
 *     another failure means it is still happening.
 *
 * The cache is read BEFORE the refetch, because a refetch that succeeds puts
 * the file in it. Every part is best-effort and the whole is capped at
 * DIAGNOSIS_BUDGET_MS, so a dead network costs the reload a second and a half,
 * not the reload.
 */
export type ChunkDiagnosis = {
  swUpdate: 'installing' | 'waiting' | 'none' | 'unknown';
  chunkCached: boolean | null;
  refetch: string | null;
  /** The refetch as a fact rather than words: did it get an answer from the server? */
  network: 'reached' | 'unreachable' | 'unknown';
};

export const DIAGNOSIS_BUDGET_MS = 1_500;

export async function diagnoseChunkFailure(chunkUrl: string | null): Promise<ChunkDiagnosis> {
  const found: ChunkDiagnosis = {
    swUpdate: 'unknown',
    chunkCached: null,
    refetch: chunkUrl ? `no answer within ${DIAGNOSIS_BUDGET_MS}ms` : null,
    network: 'unknown',
  };
  const worker = (async () => {
    try {
      const reg = await navigator.serviceWorker?.getRegistration();
      if (reg) found.swUpdate = reg.installing ? 'installing' : reg.waiting ? 'waiting' : 'none';
    } catch { /* stays unknown */ }
  })();
  const file = (async () => {
    if (!chunkUrl) return;
    try {
      if (typeof caches !== 'undefined') {
        found.chunkCached = !!(await caches.match(chunkUrl, { ignoreSearch: true }));
      }
    } catch { /* stays null */ }
    try {
      const res = await fetch(chunkUrl, { cache: 'no-store' });
      const workerOffline = !!res.headers.get('x-mucka-offline');
      found.refetch = workerOffline
        ? `${res.status} from the service worker: its own fetch failed`
        : String(res.status);
      found.network = workerOffline ? 'unreachable' : 'reached';
    } catch (err) {
      found.refetch = `failed: ${err instanceof Error ? err.message : String(err)}`;
      found.network = 'unreachable';
    }
  })();
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    Promise.all([worker, file]),
    new Promise<void>((resolve) => { timer = setTimeout(resolve, DIAGNOSIS_BUDGET_MS); }),
  ]);
  clearTimeout(timer);
  return { ...found };
}

type CaptureMessage = (
  message: string,
  context: {
    level: 'error' | 'warning';
    tags: Record<string, string>;
    fingerprint: string[];
    extra: Record<string, string | number | boolean | null>;
  },
) => unknown;

/** The two halves of `@sentry/nextjs` a chunk report uses. The boundary passes `Sentry` itself. */
export type ChunkReporter = {
  captureMessage: CaptureMessage;
  logger: { warn: (message: string, attributes: Record<string, string | number | boolean | null>) => unknown };
};

/**
 * Report a chunk failure as a MESSAGE, never through `captureException`.
 *
 * 🚨 Sentry's `chunk-load-error` inbound filter is on for this project, and it
 * drops any captured `ChunkLoadError` at ingest. So the root boundary rendered
 * its screen over working pages and, in thirty days, not one of its reports
 * arrived — while the hourly drop counts showed chunk-load errors being
 * filtered in the same hour as a MUCKA-WEB-A2 event. A report thrown away
 * after it is sent is the browser `console.error` trap one layer further
 * along (CLAUDE.md #13).
 *
 * Constant title, explicit fingerprint per boundary, the error's own words in
 * `extra`. The title must never contain a phrase the filter matches.
 *
 * ── SPLIT BY OUTCOME: A RESCUE IS NOT AN ISSUE ────────────────────────────
 *
 * A screen somebody read (`shown`, `shown_after_reload`) is an error, filed
 * per boundary exactly as before. One nobody saw (`reloaded`, `left`) is a
 * structured LOG line (`Sentry.logger.warn`), searchable and counted, raising
 * nothing — the same treatment as the stuck-navigation recovery, and for the
 * same reason (Tom, 10 Oct 2026: report failures, not rescues).
 *
 * It took two goes. Filed as an error in the error issue, every reload that
 * WORKED reopened MUCKA-WEB-CV: the only event in the first day after the
 * reload shipped was `boundary_outcome=reloaded` (12 Oct 2026). Moved to a
 * warning under a fingerprint of its own, it became MUCKA-WEB-D3, an issue
 * that only ever counted rescues and came back as "regressed" whenever one
 * worked: on 20 Oct the stuck-navigation recovery logged its full page load
 * and, 715ms later, the chunk that load cancelled was filed there as `left`.
 * A warning is still an issue on the board.
 *
 * The log carries the same fields, tags and extra together, and the old
 * issue's title as its message so one search finds both. The rate can still
 * be read off the logs; a chunk that is really missing still fails on the
 * reloaded page and arrives as `shown_after_reload`, an error.
 *
 * `shown_after_reload` stays an error in the error issue on purpose: the
 * reload ran and the chunk failed on the fresh page too, which is the case
 * worth waking up for.
 *
 * `offline` is a screen somebody read, but the designed one: the device had
 * no network, so no file could be fetched by anything. A warning under its own
 * fingerprint, so a missing chunk is never buried under laptops opened before
 * the wifi joined (MUCKA-WEB-D3), and the count still shows.
 */
export const CHUNK_SHOWN_TITLE = '[boundary] a piece of the app could not be fetched, so the error screen replaced the page';
export const CHUNK_UNSEEN_TITLE = '[boundary] a piece of the app could not be fetched, and the page was loaded again before anybody saw it';
export const CHUNK_OFFLINE_TITLE = '[boundary] a piece of the app could not be fetched because the device was offline';
export const CHUNK_OFFLINE_FINGERPRINT: readonly string[] = ['chunk-load-offline'];

export function reportChunkLoadFailure(
  sentry: ChunkReporter,
  boundary: string,
  error: { name?: string; message?: string },
  outcome: ChunkOutcome,
  diagnosis?: ChunkDiagnosis,
  leaving: PageLeavingNow = null,
): void {
  let swControlled: boolean | null = null;
  try { swControlled = !!navigator.serviceWorker?.controller; } catch { /* keep null */ }
  const tags: Record<string, string> = {
    error_boundary: boundary,
    area: 'chunk-load',
    // `reloaded` nobody saw; a route flipping to mostly `shown_after_reload`
    // is a chunk that really is not there, not a blip.
    boundary_outcome: outcome,
    // Searchable, unlike `extra`: whether a deploy's worker install was in
    // the same seconds is the first question this issue asks.
    sw_update: diagnosis?.swUpdate ?? 'unknown',
    navigator_online: typeof navigator === 'undefined' ? 'unknown' : String(navigator.onLine),
    boundary_route: typeof window === 'undefined' ? 'unknown' : window.location.pathname,
    // Which of our own full-page loads was already replacing the page, if
    // any — always set on `left`. This boundary's own reload is marked after
    // the report, so it never names itself.
    page_leaving: leaving?.reason ?? 'none',
  };
  const extra: Record<string, string | number | boolean | null> = {
    errorName: error.name ?? null,
    errorMessage: error.message ?? null,
    chunkUrl: chunkUrlOf(error),
    swControlled,
    chunkCached: diagnosis?.chunkCached ?? null,
    refetch: diagnosis?.refetch ?? null,
    pageLeavingMsAgo: leaving?.msAgo ?? null,
  };
  if (unseen(outcome)) {
    // 🚨 Never let the log stop the reload: the caller reloads straight after.
    try {
      sentry.logger.warn(CHUNK_UNSEEN_TITLE, { ...tags, ...extra });
    } catch { /* the rescue goes ahead whether or not the log does */ }
    return;
  }
  const offline = outcome === 'offline';
  sentry.captureMessage(offline ? CHUNK_OFFLINE_TITLE : CHUNK_SHOWN_TITLE, {
    level: offline ? 'warning' : 'error',
    tags,
    fingerprint: offline ? [...CHUNK_OFFLINE_FINGERPRINT] : ['boundary-chunk-load', boundary],
    extra,
  });
}
